/*
 * Knowledge-base enhancements (BetterDocs / /a/ app proxy).
 *
 * The KB is rendered server-side by the BetterDocs app and dropped whole
 * into {{ content_for_layout }}, so Liquid cannot reach inside it. Anything
 * the theme needs to change in that markup has to happen here.
 *
 * App DOM this relies on:
 *
 *   .bdl2-main-content[.bdl2-single-doc]        page root
 *     .bdl2-col-sidebar .bdl2-sidebar             category tree
 *       li.sidebar-item.sidebar-item-dropdown
 *         .sidebar-link-wrap[onclick]             toggle (stays a <div>:
 *           h2.sidebar-link                       app CSS matches
 *         ul.bdl2-sidebar-submenu                 :has(> div.expand))
 *           a.menu-link
 *     .bdl2-col-content-block .bdl2-content-block
 *       .bdl2-content-wrapper
 *         .bdl2-breadcrumb > a.breadcrumb-link
 *         h1.doc-title
 *         .bdl2-doc-meta > p.last-update, .bdl2-print
 *         #printable_area                         article body
 *         .bdl2-doc-feedback
 *       .bdl2-toc-block h3.toc-title, .toc-wrapper
 *
 * Expanded state is `.expand` on .sidebar-link-wrap; the submenu is its
 * adjacent sibling. Desktop layout is gated on a `modeDesktop` class the
 * app adds to .bdl2-main-content at runtime.
 *
 * Every step is independently guarded — if the app changes its markup a
 * step goes quiet rather than taking the page down with it.
 */
(function () {
  'use strict';

  var root = document.querySelector('.bdl2-main-content');
  if (!root) return;

  var isSingleDoc = root.classList.contains('bdl2-single-doc');

  function each(list, fn) {
    Array.prototype.forEach.call(list || [], fn);
  }

  /* ---------------------------------------------------------------
   * 1. Sticky-header offset
   * ---------------------------------------------------------------
   * The sidebar and TOC are sticky. The site header goes
   * position:fixed once .is-sticky lands on it, so a top of 0 puts
   * both columns underneath it. Publish the real height as
   * --kb-header-h for custom.css to offset against.
   * --------------------------------------------------------------- */
  function syncHeaderHeight() {
    var header =
      document.querySelector('.header-sticky-wrapper') ||
      document.querySelector('.site-header');
    if (!header) return;
    var h = header.offsetHeight;
    // Clamp: a promo bar or a mid-transition measurement can report a
    // height that would push the columns off-screen.
    if (!h || h < 50) h = 50;
    if (h > 170) h = 170;
    document.documentElement.style.setProperty('--kb-header-h', h + 'px');
  }

  syncHeaderHeight();
  window.addEventListener('resize', syncHeaderHeight);
  window.addEventListener('load', syncHeaderHeight);

  /* ---------------------------------------------------------------
   * 2. Authorship
   * ---------------------------------------------------------------
   * snippets/kb-author.liquid parks the byline and author card in a
   * hidden .kb-author-source block. Move them into the app's markup:
   * byline into .bdl2-doc-meta next to "Updated on", card after the
   * article and before the "Was it helpful?" widget.
   * --------------------------------------------------------------- */
  function placeAuthorship() {
    if (!isSingleDoc) return;

    var source = document.querySelector('[data-kb-author-source]');
    if (!source) return;

    var byline = source.querySelector('[data-kb-byline] .kb-byline');
    var card = source.querySelector('[data-kb-author-card] .kb-author-card');

    var meta = document.querySelector('.bdl2-content-wrapper .bdl2-doc-meta');
    if (byline && meta && !meta.querySelector('.kb-byline')) {
      meta.insertBefore(byline, meta.firstChild);
      // Separator between the byline and the app's "Updated on" line.
      var lastUpdate = meta.querySelector('.last-update');
      if (lastUpdate) {
        var sep = document.createElement('span');
        sep.className = 'kb-byline__sep';
        sep.setAttribute('aria-hidden', 'true');
        sep.textContent = '·';
        meta.insertBefore(sep, lastUpdate);
      }
      meta.classList.add('kb-has-byline');
    }

    // End of .bdl2-content-wrapper puts the card directly under the
    // article. Note the app renders its "Was it helpful?" widget inside
    // .bdl2-toc-block, not here, so there is nothing to insert ahead of.
    var wrapper = document.querySelector('.bdl2-content-wrapper');
    if (card && wrapper && !wrapper.querySelector('.kb-author-card')) {
      var feedback = wrapper.querySelector('.bdl2-doc-feedback');
      if (feedback && feedback.parentNode === wrapper) {
        wrapper.insertBefore(card, feedback);
      } else {
        wrapper.appendChild(card);
      }
    }

    // The parked container is empty now; drop it so it can't be found
    // by anything walking the DOM.
    if (!source.querySelector('.kb-byline, .kb-author-card')) {
      source.parentNode && source.parentNode.removeChild(source);
    }
  }

  /* ---------------------------------------------------------------
   * 2b. Article header: reading time
   * ---------------------------------------------------------------
   * The meta line carried only "Updated on <date>". A length cue is
   * the single most useful thing to add there — these articles range
   * from a couple of hundred words to well over fifteen hundred, and
   * nothing on the page said which one you had opened.
   * --------------------------------------------------------------- */
  function articleBody() {
    return (
      document.getElementById('printable_area') ||
      document.querySelector('.bdl2-content-wrapper .description')
    );
  }

  function addReadingTime() {
    if (!isSingleDoc) return;

    var meta = document.querySelector('.bdl2-content-wrapper .bdl2-doc-meta');
    var body = articleBody();
    if (!meta || !body || meta.querySelector('.kb-readtime')) return;

    var words = (body.textContent || '').trim().split(/\s+/).length;
    if (words < 40) return;

    var minutes = Math.max(1, Math.round(words / 200));

    var el = document.createElement('span');
    el.className = 'kb-readtime';
    el.innerHTML =
      '<svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" ' +
      'stroke-width="1.6" aria-hidden="true" focusable="false">' +
      '<circle cx="8" cy="8" r="6.4"/><path d="M8 4.4V8l2.6 1.6"/></svg>' +
      '<span>' + minutes + ' min read</span>';

    var lastUpdate = meta.querySelector('.last-update');
    if (lastUpdate && lastUpdate.nextSibling) {
      meta.insertBefore(el, lastUpdate.nextSibling);
    } else {
      meta.appendChild(el);
    }
  }

  /* ---------------------------------------------------------------
   * 2c. Move the feedback widget under the article
   * ---------------------------------------------------------------
   * The app renders "Was it helpful?" as a child of .bdl2-toc-block,
   * so the article's own feedback prompt sits in the right-hand
   * sidebar under the contents list — a long way from the end of the
   * thing it is asking about, and inside a sticky column. Move it to
   * where a reader finishes reading.
   * --------------------------------------------------------------- */
  function relocateFeedback() {
    if (!isSingleDoc) return;

    var feedback = document.querySelector('.bdl2-toc-block .bdl2-doc-feedback');
    var wrapper = document.querySelector('.bdl2-content-wrapper');
    if (!feedback || !wrapper) return;

    feedback.classList.add('kb-feedback--moved');

    // After the author card if it has already been placed, else at the end.
    var card = wrapper.querySelector('.kb-author-card');
    if (card && card.nextSibling) {
      wrapper.insertBefore(feedback, card.nextSibling);
    } else {
      wrapper.appendChild(feedback);
    }
  }

  /* ---------------------------------------------------------------
   * 2d. Let wide tables scroll instead of overflowing
   * ---------------------------------------------------------------
   * Comparison tables are written straight into the article with an
   * inline width:100% and no wrapper, so on a narrow column an extra
   * spec column pushes past the edge. Give each one its own scroll
   * container — CSS alone can't add the wrapper.
   * --------------------------------------------------------------- */
  function wrapTables() {
    var body = articleBody();
    if (!body) return;

    each(body.querySelectorAll('table'), function (table) {
      if (table.parentNode && table.parentNode.classList.contains('kb-table-scroll')) return;
      var box = document.createElement('div');
      box.className = 'kb-table-scroll';
      box.setAttribute('tabindex', '0');
      box.setAttribute('role', 'region');
      box.setAttribute('aria-label', 'Table, scrollable');
      table.parentNode.insertBefore(box, table);
      box.appendChild(table);
    });
  }

  /* ---------------------------------------------------------------
   * 2e. Back-to-top at the foot of the contents panel
   * --------------------------------------------------------------- */
  function addTocFooter() {
    var area = document.querySelector('.bdl2-toc-block .toc-area');
    if (!area || area.querySelector('.kb-toc-top')) return;

    var a = document.createElement('a');
    a.className = 'kb-toc-top';
    a.href = '#';
    a.innerHTML =
      '<svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" ' +
      'stroke-width="1.8" aria-hidden="true" focusable="false">' +
      '<path d="M8 13V3.5M4 7l4-4 4 4"/></svg><span>Back to top</span>';
    a.addEventListener('click', function (e) {
      e.preventDefault();
      window.scrollTo({
        top: 0,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
          ? 'auto'
          : 'smooth'
      });
    });
    area.appendChild(a);
  }

  /* ---------------------------------------------------------------
   * 3. Sidebar: document outline
   * ---------------------------------------------------------------
   * The app marks all 75 category labels as <h2>. On an article page
   * every one of them precedes the article's own <h1>, so the outline
   * a crawler or screen-reader sees is 75 sibling h2s and then, at the
   * very end, the h1 the page is actually about. Swap them for spans;
   * navigation labels are not content headings.
   *
   * Keep the sidebar-link class so the app's CSS and any of its
   * selectors still match.
   * --------------------------------------------------------------- */
  function demoteSidebarHeadings() {
    var labels = document.querySelectorAll(
      '.bdl2-sidebar .sidebar-menu h1.sidebar-link,' +
        '.bdl2-sidebar .sidebar-menu h2.sidebar-link,' +
        '.bdl2-sidebar .sidebar-menu h3.sidebar-link'
    );
    each(labels, function (el) {
      var span = document.createElement('span');
      span.className = el.className + ' kb-sidebar-label';
      span.innerHTML = el.innerHTML;
      el.parentNode.replaceChild(span, el);
    });
  }

  // Same problem, smaller: the TOC heading is an <h3> mid-article.
  function demoteTocHeading() {
    var title = document.querySelector('.bdl2-toc-block .toc-area h3.toc-title');
    if (!title) return;
    var span = document.createElement('span');
    span.className = title.className + ' kb-toc-title';
    span.innerHTML = title.innerHTML;
    title.parentNode.replaceChild(span, title);
  }

  /* ---------------------------------------------------------------
   * 4. Sidebar: keyboard + screen-reader access
   * ---------------------------------------------------------------
   * The category toggles are bare <div onclick="...">: not focusable,
   * no role, no state. Keep the div (app CSS matches
   * `:has(> div.expand)`) and add the semantics on top.
   * --------------------------------------------------------------- */
  function makeTogglesAccessible() {
    var toggles = document.querySelectorAll(
      '.bdl2-sidebar .sidebar-item-dropdown > .sidebar-link-wrap'
    );

    each(toggles, function (toggle) {
      var submenu = toggle.nextElementSibling;
      if (!submenu || !submenu.classList.contains('bdl2-sidebar-submenu')) return;

      toggle.setAttribute('role', 'button');
      toggle.setAttribute('tabindex', '0');
      toggle.setAttribute(
        'aria-expanded',
        toggle.classList.contains('expand') ? 'true' : 'false'
      );

      if (!submenu.id) {
        submenu.id =
          'kb-submenu-' + Math.random().toString(36).slice(2, 9);
      }
      toggle.setAttribute('aria-controls', submenu.id);

      var label = toggle.querySelector('.sidebar-link, .kb-sidebar-label');
      if (label && !toggle.getAttribute('aria-label')) {
        toggle.setAttribute('aria-label', label.textContent.trim());
      }

      toggle.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
          e.preventDefault();
          toggle.click();
        }
      });
    });

    // The app toggles .expand itself, so mirror it into aria-expanded
    // rather than trying to own the state.
    if (typeof MutationObserver === 'function') {
      var observer = new MutationObserver(function (records) {
        records.forEach(function (record) {
          var el = record.target;
          if (el.getAttribute('role') !== 'button') return;
          el.setAttribute(
            'aria-expanded',
            el.classList.contains('expand') ? 'true' : 'false'
          );
        });
      });
      each(toggles, function (toggle) {
        observer.observe(toggle, {
          attributes: true,
          attributeFilter: ['class']
        });
      });
    }
  }

  /* ---------------------------------------------------------------
   * 5. Sidebar: scroll the current doc into view
   * ---------------------------------------------------------------
   * The app already marks the current article with .active-item and
   * expands its category server-side, so this is not about finding it —
   * it is about the fact that the expanded category can sit anywhere in
   * a 75-item list. Once the nav has its own scroll box (see custom.css)
   * that position is off-screen more often than not, so scroll to it.
   *
   * aria-current is added for screen readers, and the expand/mark steps
   * are kept as a fallback in case the app stops doing them.
   * --------------------------------------------------------------- */
  function revealCurrentDoc() {
    if (!isSingleDoc) return;

    var sidebar = document.querySelector('.bdl2-col-sidebar .bdl2-sidebar');
    if (!sidebar) return;

    var here = window.location.pathname.replace(/\/+$/, '');
    var current = null;

    each(sidebar.querySelectorAll('a.menu-link[href]'), function (link) {
      if (current) return;
      var path;
      try {
        path = new URL(link.href, window.location.origin).pathname;
      } catch (e) {
        return;
      }
      if (path.replace(/\/+$/, '') === here) current = link;
    });

    if (!current) return;

    current.classList.add('active-item');
    current.setAttribute('aria-current', 'page');

    var item = current.closest('.sidebar-item');
    if (item) item.classList.add('active');

    // Open the owning category via the app's own toggle so its state
    // stays consistent with however it tracks expansion.
    var submenu = current.closest('.bdl2-sidebar-submenu');
    if (submenu) {
      var toggle = submenu.previousElementSibling;
      if (
        toggle &&
        toggle.classList.contains('sidebar-link-wrap') &&
        !toggle.classList.contains('expand')
      ) {
        toggle.click();
      }
    }

    // Scroll the sidebar's own scroll box — not the window. Anything
    // that walks up to the document (scrollIntoView) would yank the
    // reader down the page on load.
    window.setTimeout(function () {
      var box = sidebar;
      if (box.scrollHeight <= box.clientHeight) return;
      var offset = current.offsetTop - box.offsetTop;
      box.scrollTop = Math.max(0, offset - box.clientHeight / 3);
    }, 350); // after the app's max-height expand transition (.3s)
  }

  /* ---------------------------------------------------------------
   * 6. Sidebar: category filter
   * ---------------------------------------------------------------
   * 75 categories is more than anyone scans. Type to narrow the list
   * by category name, or by the title of any article inside it.
   * --------------------------------------------------------------- */
  function addCategoryFilter() {
    var wrapper = document.querySelector('.bdl2-col-sidebar .bdl2-sidebar-wrapper');
    var sidebar = document.querySelector('.bdl2-col-sidebar .bdl2-sidebar');
    if (!wrapper || !sidebar) return;
    if (wrapper.querySelector('.kb-cat-filter')) return;

    var items = sidebar.querySelectorAll('.sidebar-menu > .sidebar-item');
    if (items.length < 8) return; // not worth a filter on a short list

    // Cache the searchable text once — re-reading 380 links per
    // keystroke is what makes filters feel slow.
    var entries = [];
    each(items, function (item) {
      entries.push({
        el: item,
        text: (item.textContent || '').toLowerCase().replace(/\s+/g, ' ')
      });
    });

    var filter = document.createElement('div');
    filter.className = 'kb-cat-filter';
    filter.innerHTML =
      '<label class="visually-hidden" for="kb-cat-filter-input">Filter categories</label>' +
      '<input id="kb-cat-filter-input" class="kb-cat-filter__input" type="search" ' +
      'placeholder="Filter categories…" autocomplete="off" spellcheck="false">' +
      '<button type="button" class="kb-cat-filter__clear" aria-label="Clear filter">&times;</button>' +
      '<p class="kb-cat-filter__empty" role="status">No categories match.</p>';

    // Below the app's search button, above the tree.
    var searchBar = wrapper.querySelector('.bdl2-search-bar');
    if (searchBar && searchBar.nextSibling) {
      wrapper.insertBefore(filter, searchBar.nextSibling);
    } else {
      wrapper.insertBefore(filter, sidebar);
    }

    var input = filter.querySelector('.kb-cat-filter__input');
    var clear = filter.querySelector('.kb-cat-filter__clear');

    function apply() {
      var q = input.value.trim().toLowerCase();
      filter.classList.toggle('is-filtering', q.length > 0);

      if (!q) {
        entries.forEach(function (entry) {
          entry.el.removeAttribute('data-kb-filtered');
        });
        filter.classList.remove('is-empty');
        return;
      }

      var hits = 0;
      entries.forEach(function (entry) {
        var match = entry.text.indexOf(q) !== -1;
        if (match) hits++;
        entry.el.setAttribute('data-kb-filtered', match ? 'in' : 'out');
      });
      filter.classList.toggle('is-empty', hits === 0);
    }

    input.addEventListener('input', apply);
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        input.value = '';
        apply();
      }
    });
    clear.addEventListener('click', function () {
      input.value = '';
      apply();
      input.focus();
    });
  }

  /* ---------------------------------------------------------------
   * 7. Breadcrumb: keep "Home" on this domain
   * ---------------------------------------------------------------
   * The app renders the first crumb against the raw myshopify URL
   * (buybinding.myshopify.com), so the site's own breadcrumb sends
   * readers and crawlers off to the unbranded storefront.
   * --------------------------------------------------------------- */
  function fixBreadcrumbHome() {
    each(
      document.querySelectorAll('.bdl2-breadcrumb a.breadcrumb-link[href]'),
      function (link) {
        if (/\.myshopify\.com/i.test(link.href)) {
          link.href = window.location.origin + '/';
        }
      }
    );
  }

  /* ---------------------------------------------------------------
   * 8. Structured data
   * ---------------------------------------------------------------
   * snippets/kb-author.liquid emits the TechArticle base (headline,
   * description, url, author, publisher) from what Liquid can see.
   * The rest only exists in the app's DOM, so add it here:
   *   - dateModified from "Updated on <date>"
   *   - BreadcrumbList from the app's breadcrumb
   *   - FAQPage from an FAQ section, if the article has one
   * --------------------------------------------------------------- */
  function enrichSchema() {
    var node = document.getElementById('kb-article-schema');
    if (!node) return;

    // kb-author.liquid gates the schema on the URL shape. If that guess
    // was wrong and this is a listing rather than a doc, take the article
    // markup back off the page rather than publishing it.
    if (!isSingleDoc) {
      node.parentNode && node.parentNode.removeChild(node);
      return;
    }

    var article;
    try {
      article = JSON.parse(node.textContent);
    } catch (e) {
      return;
    }

    // -- dateModified ------------------------------------------------
    var lastUpdate = document.querySelector('.bdl2-doc-meta .last-update');
    if (lastUpdate) {
      var raw = lastUpdate.textContent.replace(/^\s*updated\s+on\s+/i, '').trim();
      var parsed = new Date(raw);
      if (!isNaN(parsed.getTime())) {
        // "Jun 02, 2026" parses as local midnight, so toISOString() would
        // shift it to Jun 1 for any reader east of UTC. The source has no
        // time component anyway — emit a plain YYYY-MM-DD date, which is
        // a valid schema.org Date and carries no timezone to get wrong.
        var mm = String(parsed.getMonth() + 1);
        var dd = String(parsed.getDate());
        article.dateModified =
          parsed.getFullYear() +
          '-' + (mm.length < 2 ? '0' + mm : mm) +
          '-' + (dd.length < 2 ? '0' + dd : dd);
        // The app exposes no published date anywhere, so the modified
        // date stands in for both. Leaving datePublished out is worse:
        // Google then has no date for the article at all.
        if (!article.datePublished) article.datePublished = article.dateModified;
      }
    }

    // -- article section / word count --------------------------------
    var body = document.getElementById('printable_area') ||
      document.querySelector('.bdl2-content-wrapper .description');
    if (body) {
      var words = (body.textContent || '').trim().split(/\s+/).length;
      if (words > 1) article.wordCount = words;
    }

    var crumbs = document.querySelectorAll('.bdl2-breadcrumb .breadcrumb-link');
    if (crumbs.length > 1) {
      var category = crumbs[crumbs.length - 2];
      if (category) article.articleSection = category.textContent.trim();
    }

    var graph = [article];

    // -- BreadcrumbList ----------------------------------------------
    if (crumbs.length) {
      var itemList = [];
      each(crumbs, function (crumb, i) {
        var name = crumb.textContent.trim();
        if (!name) return;
        // The app truncates the last crumb ("Spiral Coil Binding...").
        // Use the real h1 for it instead.
        var isLast = i === crumbs.length - 1;
        if (isLast) {
          var h1 = document.querySelector('.bdl2-content-wrapper h1.doc-title');
          if (h1) name = h1.textContent.trim();
        }
        var entry = {
          '@type': 'ListItem',
          position: itemList.length + 1,
          name: name
        };
        var href = crumb.getAttribute('href');
        if (href) {
          entry.item = /\.myshopify\.com/i.test(href)
            ? window.location.origin + '/'
            : new URL(href, window.location.origin).href;
        } else {
          entry.item = window.location.href.split('#')[0];
        }
        itemList.push(entry);
      });

      if (itemList.length) {
        graph.push({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: itemList
        });
      }
    }

    // -- FAQPage -----------------------------------------------------
    // Many KB articles end in a "Frequently Asked Questions" section:
    // an h2, then one h3 per question with the answer following.
    if (body) {
      var faqHeading = null;
      each(body.querySelectorAll('h2, h3'), function (h) {
        if (faqHeading) return;
        if (/^\s*(frequently asked questions|faqs?)\s*$/i.test(h.textContent)) {
          faqHeading = h;
        }
      });

      if (faqHeading) {
        // Two patterns are in use across the knowledge base and both have
        // to be read, or half the FAQ sections produce no markup:
        //   A. <h3>Question</h3> followed by the answer as siblings
        //   B. <p><strong>Question</strong><br>Answer</p>
        var questionLevel = faqHeading.tagName === 'H2' ? 'H3' : 'H4';
        var faqs = [];

        function pushFaq(question, answer) {
          question = (question || '').trim();
          answer = (answer || '').trim();
          if (!question || !answer) return;
          faqs.push({
            '@type': 'Question',
            name: question,
            acceptedAnswer: { '@type': 'Answer', text: answer }
          });
        }

        var node2 = faqHeading.nextElementSibling;
        while (node2) {
          // A heading at or above the FAQ heading's level ends the section.
          if (node2.tagName === faqHeading.tagName || node2.tagName === 'H1') break;

          // -- pattern A ------------------------------------------------
          if (node2.tagName === questionLevel) {
            var answerA = [];
            var walk = node2.nextElementSibling;
            while (
              walk &&
              walk.tagName !== questionLevel &&
              walk.tagName !== faqHeading.tagName &&
              walk.tagName !== 'H1'
            ) {
              var text = (walk.textContent || '').trim();
              if (text) answerA.push(text);
              walk = walk.nextElementSibling;
            }
            pushFaq(node2.textContent, answerA.join(' '));
          }

          // -- pattern B ------------------------------------------------
          // A paragraph that opens with <strong> is a question; the rest
          // of that paragraph's text is the answer.
          if (node2.tagName === 'P') {
            var lead = node2.firstElementChild;
            if (lead && (lead.tagName === 'STRONG' || lead.tagName === 'B')) {
              var q = lead.textContent;
              var whole = node2.textContent || '';
              var a = whole.slice(whole.indexOf(q) + q.length);
              pushFaq(q, a);
            }
          }

          node2 = node2.nextElementSibling;
        }

        if (faqs.length) {
          graph.push({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs
          });
        }
      }
    }

    node.textContent = JSON.stringify(graph.length === 1 ? graph[0] : graph);
  }


  /* ---------------------------------------------------------------
   * 10. Utility bar
   * ---------------------------------------------------------------
   * Breadcrumb, KB search and Print each sat in a different place —
   * the crumb inside the article, print in the doc meta row, search
   * only in the sidebar (so it is gone on mobile, where the sidebar
   * is a drawer). One bar across the top of the container collects
   * them, and the article then opens with its own title.
   *
   * The search button forwards to the app's own search trigger
   * rather than reimplementing the modal.
   * --------------------------------------------------------------- */
  var ICON_SEARCH =
    '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M10 2a8 8 0 105 14.3l5.3 5.4 1.4-1.4-5.4-5.3A8 8 0 0010 2zm0 2a6 6 0 110 12 6 6 0 010-12z"/></svg>';
  var ICON_PRINT =
    '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M18 8H6V3h12v5zm2 2H4a2 2 0 00-2 2v6h4v4h12v-4h4v-6a2 2 0 00-2-2zm-4 10H8v-5h8v5z"/></svg>';

  function buildUtilityBar() {
    var container = root.querySelector('.bdl2-container');
    if (!container || container.querySelector('.kb-utilbar')) return;
    var row = container.querySelector('.bdl2-row');
    if (!row) return;

    var bar = document.createElement('div');
    bar.className = 'kb-utilbar';

    var crumb = root.querySelector('.bdl2-breadcrumb');
    if (crumb) bar.appendChild(crumb);

    var spacer = document.createElement('span');
    spacer.className = 'kb-utilbar__spacer';
    bar.appendChild(spacer);

    // Forward to the app's search trigger, wherever it currently lives.
    var appSearch = root.querySelector('.bdl2-search-bar .sidebar-search') ||
                    root.querySelector('.bdl2-search-bar');
    if (appSearch) {
      var search = document.createElement('button');
      search.type = 'button';
      search.className = 'kb-utilbar__btn kb-utilbar__search';
      search.innerHTML = ICON_SEARCH + '<span>Search the knowledge base</span><kbd>/</kbd>';
      search.addEventListener('click', function () { appSearch.click(); });
      bar.appendChild(search);
    }

    var print = root.querySelector('.bdl2-print');
    if (print) {
      print.classList.add('kb-utilbar__btn');
      print.innerHTML = ICON_PRINT + '<span>Print</span>';
      bar.appendChild(print);
    }

    container.insertBefore(bar, row);
  }

  /* ---------------------------------------------------------------
   * 11. Archive header
   * ---------------------------------------------------------------
   * The app emits .icon, h1.title and h5.subtitle as three bare
   * siblings, which cannot be laid out as "icon beside a column of
   * text" without a wrapper. Add one, then hang the stats off it:
   * the doc count the app already prints, plus the most recent
   * "Last Updated" read off the rows.
   * --------------------------------------------------------------- */
  function enhanceArchiveHeader() {
    var banner = root.querySelector('.bdl2-archive .bdl2-banner');
    if (!banner || banner.querySelector('.kb-banner__body')) return;

    var title = banner.querySelector('.title');
    if (!title) return;
    var subtitle = banner.querySelector('.subtitle');

    var body = document.createElement('div');
    body.className = 'kb-banner__body';
    banner.insertBefore(body, title);
    body.appendChild(title);
    if (subtitle) body.appendChild(subtitle);

    if (subtitle && /^\s*\d+\s*docs?\s*$/i.test(subtitle.textContent || '')) {
      subtitle.parentNode.removeChild(subtitle);
      subtitle = null;
    }

    var stats = document.createElement('div');
    stats.className = 'kb-banner__stats';

    var rows = root.querySelectorAll('.bdl2-archive-single-doc-block .archive-single-doc');
    if (rows.length) {
      stats.appendChild(chip(rows.length + (rows.length === 1 ? ' article' : ' articles')));
    }

    // "Last Updated: 1st June, 2026" — take the first row's stamp; the
    // app already orders newest first.
    var stamp = root.querySelector('.bdl2-archive-single-doc-block .archive-single-doc .updated');
    if (stamp) {
      var text = (stamp.textContent || '').replace(/^\s*Last Updated:\s*/i, '').trim();
      if (text) stats.appendChild(chip('Updated ' + text));
    }

    if (stats.children.length) body.appendChild(stats);

    function chip(text) {
      var s = document.createElement('span');
      s.className = 'kb-banner__stat';
      s.textContent = text;
      return s;
    }
  }

  /* ---------------------------------------------------------------
   * 12. Injected bands
   * ---------------------------------------------------------------
   * Related docs and the category grid are built from links already
   * in the sidebar, so they need no network call and cannot go stale
   * against the tree. The product band is the one that fetches.
   *
   * All three are decoration, not content: they are appended after
   * the app's own markup, and if any selector misses, the page is
   * simply the page without that band.
   * --------------------------------------------------------------- */
  function makeBand(titleText, hintHtml, gridClass) {
    var band = document.createElement('section');
    band.className = 'kb-band';
    var head = document.createElement('div');
    head.className = 'kb-band__head';
    var h2 = document.createElement('h2');
    h2.className = 'kb-band__title';
    h2.textContent = titleText;
    head.appendChild(h2);
    if (hintHtml) {
      var hint = document.createElement('p');
      hint.className = 'kb-band__hint';
      hint.innerHTML = hintHtml;
      head.appendChild(hint);
    }
    band.appendChild(head);
    var grid = document.createElement('div');
    grid.className = gridClass;
    band.appendChild(grid);
    band.kbGrid = grid;
    return band;
  }

  // Where a band should be appended on each page type.
  function bandHost() {
    return root.querySelector('.bdl2-archive-wrapper') ||
           root.querySelector('.bdl2-content-wrapper');
  }

  // The sidebar item for the CATEGORY being viewed.
  //
  // On a single doc the app marks the document's own <li> as .active,
  // which is a leaf inside the category's submenu — so reading the
  // active item directly gave the doc title where the category name was
  // wanted, and a submenu with nothing in it. Climb to the nearest
  // ancestor that actually owns a submenu.
  function activeCategoryItem() {
    var marker =
      root.querySelector('.bdl2-sidebar .sidebar-item.active') ||
      root.querySelector('.bdl2-sidebar .menu-link.active-item') ||
      root.querySelector('.bdl2-sidebar .sidebar-link-wrap.expand');
    if (!marker) return null;

    var node = marker.closest ? marker.closest('.sidebar-item') : null;
    while (node) {
      if (node.querySelector('.bdl2-sidebar-submenu')) return node;
      node = node.parentNode && node.parentNode.closest
        ? node.parentNode.closest('.sidebar-item')
        : null;
    }
    // A category with no submenu rendered is still the right answer.
    return marker.closest ? marker.closest('.sidebar-item') : null;
  }

  function buildRelatedDocs() {
    if (!isSingleDoc) return;
    var host = bandHost();
    if (!host || host.querySelector('.kb-doc-grid')) return;

    var item = activeCategoryItem();
    if (!item) return;

    var links = item.querySelectorAll('.bdl2-sidebar-submenu .menu-link');
    var here = location.pathname.replace(/\/+$/, '');
    var picked = [];
    each(links, function (a) {
      if (picked.length >= 3) return;
      var href = a.getAttribute('href') || '';
      if (!href) return;
      // The app flags the current doc in the tree; trust that first, and
      // fall back to comparing paths.
      if (a.classList.contains('active-item')) return;
      var path = href.replace(/^https?:\/\/[^/]+/, '').replace(/\/+$/, '');
      if (path === here) return;
      picked.push({ href: href, title: (a.textContent || '').trim() });
    });
    if (!picked.length) return;

    var catLink = item.querySelector('.sidebar-link-wrap a, a.sidebar-link');
    var band = makeBand(
      'More in this category',
      catLink ? '<a href="' + catLink.getAttribute('href') + '">All articles &rarr;</a>' : '',
      'kb-doc-grid'
    );
    picked.forEach(function (d) {
      var a = document.createElement('a');
      a.className = 'kb-doc-card';
      a.href = d.href;
      var t = document.createElement('span');
      t.className = 'kb-doc-card__t';
      t.textContent = d.title;
      var go = document.createElement('span');
      go.className = 'kb-doc-card__go';
      go.textContent = 'Read article →';
      a.appendChild(t);
      a.appendChild(go);
      band.kbGrid.appendChild(a);
    });
    host.appendChild(band);
  }

  function buildCategoryGrid() {
    if (isSingleDoc) return;
    var host = bandHost();
    if (!host || host.querySelector('.kb-cat-grid')) return;

    var items = root.querySelectorAll('.bdl2-sidebar .sidebar-menu > .sidebar-item');
    if (items.length < 4) return;

    var activeItem = activeCategoryItem();

    var band = makeBand('Browse other categories', '', 'kb-cat-grid');
    var used = 0;
    each(items, function (item) {
      if (used >= 16 || item === activeItem) return;
      var link = item.querySelector('a.sidebar-link, .sidebar-link-wrap a');
      var label = item.querySelector('.sidebar-link, .kb-sidebar-label');
      if (!link || !label) return;
      var count = label.querySelector('.count');
      var name = (label.textContent || '').replace(count ? count.textContent : '', '').trim();
      if (!name) return;
      used++;
      var a = document.createElement('a');
      a.className = 'kb-cat-chip';
      a.href = link.getAttribute('href');
      a.appendChild(document.createTextNode(name));
      if (count) {
        var n = document.createElement('span');
        n.className = 'kb-cat-chip__n';
        n.textContent = (count.textContent || '').trim();
        a.appendChild(n);
      }
      band.kbGrid.appendChild(a);
    });
    if (!used) return;
    host.appendChild(band);
  }

  /* Product band -----------------------------------------------------
   * The only band that needs data the page does not already have.
   * Shopify's predictive-search endpoint is same-origin from /a/, so
   * no app or metafield is involved. The query is the KB category
   * name with the words that describe a KB rather than a product
   * stripped out ("comparison", "guide", "equipment"...).
   * ------------------------------------------------------------------ */
  var QUERY_STOPWORDS = /\b(comparison|comparisons|guide|guides|basics|overview|faq|faqs|tips|equipment|machines?|general)\b/gi;

  function productQuery() {
    var source =
      (root.querySelector('.bdl2-archive .bdl2-banner .title') || {}).textContent ||
      (function () {
        var item = activeCategoryItem();
        var label = item && item.querySelector ? item.querySelector('.sidebar-link, .kb-sidebar-label') : null;
        return label ? label.textContent : '';
      })();

    var q = (source || '')
      .replace(/\d+\s*docs?/i, '')
      .replace(QUERY_STOPWORDS, ' ')
      .replace(/[^\w\s-]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    // Two or three words is the sweet spot — a whole category name
    // returns nothing, one word returns the entire catalogue.
    var words = q.split(' ').filter(Boolean).slice(0, 3);
    return words.length ? words.join(' ') : '';
  }

  /* The endpoint returns price as a bare number string ("3396.25"),
     not a formatted money value, so it has to be formatted here. */
  function formatPrice(value) {
    if (value === null || value === undefined) return '';
    var raw = String(value).trim();
    if (!raw) return '';
    // Already formatted by the endpoint ("$1,872.36") — leave it alone.
    if (/[^\d.,\s-]/.test(raw)) return raw;
    var n = parseFloat(raw.replace(/,/g, ''));
    if (!isFinite(n)) return '';
    try {
      return '$' + n.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    } catch (e) {
      return '$' + n.toFixed(2);
    }
  }

  function buildProductBand() {
    var host = bandHost();
    if (!host || host.querySelector('.kb-prod-grid')) return;
    if (!window.fetch) return;

    var q = productQuery();
    if (!q) return;

    var url = '/search/suggest.json?q=' + encodeURIComponent(q) +
      '&resources[type]=product&resources[limit]=4' +
      '&resources[options][unavailable_products]=last';

    fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        var products = data && data.resources && data.resources.results &&
                       data.resources.results.products;
        if (!products || products.length < 2) return;
        if (host.querySelector('.kb-prod-grid')) return;

        var band = makeBand(
          'Related equipment',
          '<a href="/search?q=' + encodeURIComponent(q) + '">Shop ' + q + ' &rarr;</a>',
          'kb-prod-grid'
        );

        products.slice(0, 4).forEach(function (p) {
          var a = document.createElement('a');
          a.className = 'kb-prod-card';
          a.href = p.url;

          var src = p.image || (p.featured_image && p.featured_image.url);
          if (src) {
            var img = document.createElement('img');
            img.className = 'kb-prod-card__img';
            img.src = src;
            img.alt = '';
            img.loading = 'lazy';
            img.decoding = 'async';
            a.appendChild(img);
          }

          var b = document.createElement('div');
          b.className = 'kb-prod-card__b';
          var n = document.createElement('span');
          n.className = 'kb-prod-card__n';
          n.textContent = p.title;
          b.appendChild(n);
          var price = formatPrice(p.price);
          if (price) {
            var pr = document.createElement('span');
            pr.className = 'kb-prod-card__p';
            pr.textContent = price;
            b.appendChild(pr);
          }
          a.appendChild(b);
          band.kbGrid.appendChild(a);
        });

        host.appendChild(band);
      })
      .catch(function () {});
  }

  /* --------------------------------------------------------------- */

  function init() {
    // Order matters: demote the headings before the toggles read their
    // labels, and mark the current doc before the filter caches text.
    try { demoteSidebarHeadings(); } catch (e) {}
    try { demoteTocHeading(); } catch (e) {}
    try { makeTogglesAccessible(); } catch (e) {}
    try { placeAuthorship(); } catch (e) {}
    try { addReadingTime(); } catch (e) {}
    // After placeAuthorship, so the feedback widget lands below the card.
    try { relocateFeedback(); } catch (e) {}
    try { wrapTables(); } catch (e) {}
    try { addTocFooter(); } catch (e) {}
    try { fixBreadcrumbHome(); } catch (e) {}
    try { addCategoryFilter(); } catch (e) {}
    try { revealCurrentDoc(); } catch (e) {}
    try { enrichSchema(); } catch (e) {}
    // Layout steps last: they move nodes the steps above look up.
    try { buildUtilityBar(); } catch (e) {}
    try { enhanceArchiveHeader(); } catch (e) {}
    try { buildRelatedDocs(); } catch (e) {}
    try { buildCategoryGrid(); } catch (e) {}
    try { buildProductBand(); } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
